import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import type { ChartAccountType, ChartFilters } from '@/features/accounting/api'
import { AccountingChartScreen } from '@/features/accounting/chart-screen'
import { hasGrant } from '@/lib/me'

const ACCOUNT_TYPES: ChartAccountType[] = ['asset', 'liability', 'equity', 'revenue', 'expense']
const ACCOUNT_STATUSES = ['active', 'inactive']

function isAccountType(value: unknown): value is ChartAccountType {
  return typeof value === 'string' && (ACCOUNT_TYPES as string[]).includes(value)
}

function isAccountStatus(value: unknown): value is 'active' | 'inactive' {
  return typeof value === 'string' && ACCOUNT_STATUSES.includes(value)
}

/**
 * Route shell for the chart of accounts.
 *
 * The filters and the search live in the URL search params so a filtered view
 * is linkable and survives a reload (R-14); the screen only reads and writes
 * them. There are no child routes under `/accounting/chart`, so no Outlet is
 * needed — adding one later means re-checking invariant 1 first.
 */
export const Route = createFileRoute('/_authenticated/accounting/chart')({
  validateSearch: (search: Record<string, unknown>): ChartFilters => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    type: isAccountType(search.type) ? search.type : undefined,
    status: isAccountStatus(search.status) ? search.status : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'accounting_account.view')) {
      throw redirect({ to: '/accounting' })
    }
  },
  component: AccountingChartRoute,
})

function AccountingChartRoute() {
  const navigate = useNavigate()
  const params = Route.useSearch()

  return (
    <AccountingChartScreen
      params={params}
      onParamsChange={(next) => void navigate({ to: '/accounting/chart', search: next })}
    />
  )
}
